import { useState } from 'react';
import { Search } from 'lucide-react';
import { Alert, Badge, Button, Card, CardContent, Checkbox, Field, Input, PageHeader, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, useToast } from '@salesbee/ui';
import { CA_PROVINCES, US_STATES } from '@salesbee/contracts';
import { FeatureGate, Meter, QueryView } from '../components/common';
import { errorMessage, trpc } from '../lib/trpc';

export function ProspectingPage() {
  return (
    <FeatureGate feature="prospecting">
      <Prospecting />
    </FeatureGate>
  );
}

function Prospecting() {
  const usage = trpc.prospecting.usage.useQuery();
  const lists = trpc.prospecting.lists.list.useQuery();
  const contactLists = trpc.crm.lists.list.useQuery();
  const search = trpc.prospecting.search.useMutation();
  const importMut = trpc.prospecting.importToCrm.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [q, setQ] = useState({ kind: 'business' as 'business' | 'person', industry: '', keywords: '', city: '', country: 'CA' as 'CA' | 'US', region: 'ON', titles: '', limit: '25' });
  const [listName, setListName] = useState('');
  const [activeList, setActiveList] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetList, setTargetList] = useState('');
  const prospects = trpc.prospecting.list.useQuery({ listId: activeList || undefined, status: 'new', limit: 200 });
  const set = (k: keyof typeof q) => (e: { target: { value: string } }) => setQ((x) => ({ ...x, [k]: e.target.value }));
  const regions = (q.country === 'CA' ? CA_PROVINCES : US_STATES).map((r) => ({ value: r.code, label: r.name }));

  const runSearch = () =>
    search.mutate(
      {
        query: {
          kind: q.kind,
          industry: q.industry || undefined,
          keywords: q.keywords.split(',').map((s) => s.trim()).filter(Boolean),
          city: q.city || undefined,
          region: q.region || undefined,
          country: q.country,
          titles: q.titles.split(',').map((s) => s.trim()).filter(Boolean),
          limit: Number(q.limit),
        },
        newListName: listName || `${q.industry || 'Prospects'} · ${q.city || q.region}`,
      },
      {
        onSuccess: async (r) => {
          success(`${r.saved} saved`, `${r.duplicates} duplicates skipped${r.overQuota ? `, ${r.overQuota} over your monthly quota` : ''}`);
          if (r.listId) setActiveList(r.listId);
          await Promise.all([utils.prospecting.invalidate()]);
        },
        onError: (e) => error('Search failed', errorMessage(e)),
      },
    );

  return (
    <div className="space-y-6">
      <PageHeader title="Prospecting" description="Find businesses and people by industry, location and keywords, then import them into the CRM in one click." />
      {usage.data ? (
        <Card>
          <CardContent className="py-5">
            <Meter label={`Prospects this month (provider: ${usage.data.provider})`} used={usage.data.used} limit={usage.data.limit} unit="" />
          </CardContent>
        </Card>
      ) : null}
      <Card>
        <CardContent className="grid gap-4 py-6 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Search for"><Select value={q.kind} onChange={set('kind')} options={[{ value: 'business', label: 'Businesses' }, { value: 'person', label: 'People' }]} /></Field>
          <Field label="Industry"><Input value={q.industry} onChange={set('industry')} placeholder="e.g. Property management" /></Field>
          <Field label="Keywords (comma separated)"><Input value={q.keywords} onChange={set('keywords')} /></Field>
          {q.kind === 'person' ? <Field label="Job titles"><Input value={q.titles} onChange={set('titles')} placeholder="Owner, Facilities manager" /></Field> : <Field label="Results"><Select value={q.limit} onChange={set('limit')} options={['10', '25', '50', '100'].map((v) => ({ value: v, label: v }))} /></Field>}
          <Field label="City"><Input value={q.city} onChange={set('city')} /></Field>
          <Field label="Country"><Select value={q.country} onChange={(e) => setQ((x) => ({ ...x, country: e.target.value as 'CA', region: '' }))} options={[{ value: 'CA', label: 'Canada' }, { value: 'US', label: 'United States' }]} /></Field>
          <Field label="Province / state"><Select value={q.region} onChange={set('region')} placeholder="Any" options={regions} /></Field>
          <Field label="Save to new list"><Input value={listName} onChange={(e) => setListName(e.target.value)} placeholder="Auto-named" /></Field>
          <div className="sm:col-span-2 lg:col-span-4">
            <Button onClick={runSearch} loading={search.isPending}><Search /> Search</Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Prospect list" className="min-w-64">
          <Select value={activeList} onChange={(e) => { setActiveList(e.target.value); setSelected(new Set()); }} placeholder="All lists" options={(lists.data ?? []).map((l) => ({ value: l.id, label: `${l.name} (${l.size})` }))} />
        </Field>
        <Field label="Add imported contacts to" className="min-w-64">
          <Select value={targetList} onChange={(e) => setTargetList(e.target.value)} placeholder="No list" options={(contactLists.data ?? []).filter((l) => l.kind === 'static').map((l) => ({ value: l.id, label: l.name }))} />
        </Field>
        <Button
          disabled={selected.size === 0}
          loading={importMut.isPending}
          onClick={() =>
            importMut.mutate(
              { prospectIds: [...selected], contactListId: targetList || undefined, createDeals: true },
              { onSuccess: async (r) => { success(`Imported ${r.imported} into the CRM`); setSelected(new Set()); await utils.prospecting.invalidate(); }, onError: (e) => error('Import failed', errorMessage(e)) },
            )
          }
        >
          Import {selected.size || ''} to CRM
        </Button>
      </div>
      <Alert variant="info">Business listings are imported with the “B2B (business line)” basis. In Canada an AI voice may only call contacts with express consent; use human calls for cold B2B outreach.</Alert>
      <QueryView query={prospects}>
        {(data) => (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><Checkbox aria-label="Select all" checked={data.items.length > 0 && selected.size === data.items.length} onCheckedChange={(v) => setSelected(v ? new Set(data.items.map((p) => p.id)) : new Set())} /></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>ICP score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Checkbox aria-label={`Select ${p.name}`} checked={selected.has(p.id)} onCheckedChange={(v) => setSelected((s) => { const n = new Set(s); if (v) n.add(p.id); else n.delete(p.id); return n; })} />
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{p.name}</p>
                      <p className="text-[12px] text-muted-foreground">{p.title ? `${p.title} · ${p.companyName ?? ''}` : (p.website ?? '')}</p>
                    </TableCell>
                    <TableCell>{p.industry ?? '—'}</TableCell>
                    <TableCell className="font-mono text-[13px]">{p.phone ?? '—'}</TableCell>
                    <TableCell>{[p.city, p.region].filter(Boolean).join(', ')}</TableCell>
                    <TableCell><Badge variant={p.icpScore >= 70 ? 'success' : p.icpScore >= 40 ? 'warning' : 'default'} title={p.icpReasons.join(', ')}>{p.icpScore}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.items.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">No new prospects here. Run a search.</p> : null}
          </Card>
        )}
      </QueryView>
    </div>
  );
}
